// Finds a part for a BOM: parts in the same part group as the line's (`near`: another colour of the same part) first,
// then parts in the BOM's projects (a product's link, else its BOM's project; a sub-assembly's from what uses it), then
// parts in no project yet whose SKU starts with one of those projects' codes after its type prefix (MAT-GCU2-…: "GCU2-…, not
// on a BOM yet": a part just made for it), then parts sharing a tag with those projects' parts (or with parts already in the BOM), then everything
// else. Replace part ranks by the replaced part's projects (`projectIds`). Type to filter; ↑/↓ and Enter pick. `inline`:
// the list always shows under the box, in the flow (inside a pop-up, so it's measured and nothing below is covered).
import { Search } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { type BomIndex, type BomRow, componentsOf } from "../../../shared/boms.ts";
import type { Item } from "../../../shared/db.gen.ts";
import { skuStem } from "../../../shared/part-sku.ts";
import { typeLabel, typeOf } from "../../../shared/part-type.ts";
import { inProject, partProjects } from "../../../shared/projects.ts";
import { money } from "../lib/format.ts";
import { bomProjects, useProjectIndex } from "../lib/projects.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, cx, Input, Thumb } from "../ui/controls.tsx";

const LIMIT = 60;
const KIND_ORDER = { material: 0, subassembly: 1, service: 2, sell: 3 } as const;

export function PartPicker({
  bom,
  projectIds,
  exclude,
  ix,
  onPick,
  onCancel,
  placeholder = "Add a part… (SKU or name)",
  autoFocus,
  icon,
  className,
  near,
  inline,
}: {
  /** The BOM it's for: ranked by its projects, and its own product or sub-assembly left out. */
  bom?: BomRow;
  /** Without a BOM (Replace part): rank by these projects and leave `exclude` out. */
  projectIds?: readonly string[];
  exclude?: string;
  ix: BomIndex;
  /** Part groups --- parts the line uses now: their groups' parts come first ("Same group"). */
  near?: readonly (string | null)[];
  onPick: (item: Item) => void;
  onCancel?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  icon?: ReactNode;
  className?: string;
  inline?: boolean;
}) {
  const items = useRows("item");
  const partGroups = useRows("part_group");
  const px = useProjectIndex();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(!!autoFocus);
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  // Rank: 0 = the same part group as the line's parts, 1 = in the BOM's projects, 2 = in no project and not on a BOM yet
  // with one of their codes as its SKU's start, 3 = shares a tag, 4 = the rest.
  const nearKey = (near ?? []).join();
  const fromBom = bom ? bomProjects(px, bom) : undefined;
  const wanted = fromBom ? [...fromBom.inUse, ...fromBom.onDraft] : (projectIds ?? []);
  const wantedKey = wanted.join();
  const codes = wanted.flatMap((id) => px.projectById.get(id)?.code || []);
  const ranked = useMemo(() => {
    const ids = wantedKey.split(",").filter(Boolean);
    const starts = ids.flatMap((id) => px.projectById.get(id)?.code || []).map((c) => `${c.toUpperCase()}-`);
    const projectsOf = partProjects(px); // every part on a BOM or in a project; one not in it is on no BOM yet
    const inOne = (i: Item) => ids.some((p) => inProject(px, i, p));
    const fresh = (i: Item) =>
      starts.length > 0 && i.source === "local" && !projectsOf.has(i.id) && starts.some((s) => skuStem(i.sku).toUpperCase().startsWith(s));
    const used = bom ? componentsOf(ix, bom.id) : new Set<string>();
    const tags = new Set<string>();
    for (const i of items) if (used.has(i.id) || (ids.length && inOne(i))) for (const t of i.tags) tags.add(t);
    const nearIds = new Set(nearKey.split(",").filter(Boolean));
    const groups = new Set(items.filter((i) => nearIds.has(i.id) && i.part_group_id).map((i) => i.part_group_id));
    const rank = (i: Item) => (i.part_group_id && groups.has(i.part_group_id) ? 0 : inOne(i) ? 1 : fresh(i) ? 2 : i.tags.some((t) => tags.has(t)) ? 3 : 4);
    const out = bom?.item_id ?? exclude;
    return items
      .filter((i) => !i.archived && i.id !== out && !(bom?.shopify_product_id && i.shopify_product_id === bom.shopify_product_id))
      .map((i) => ({ item: i, rank: rank(i), hay: `${i.sku} ${i.name} ${i.tags.join(" ")}`.toLowerCase() }))
      .sort(
        (a, b) => a.rank - b.rank || KIND_ORDER[a.item.kind] - KIND_ORDER[b.item.kind] || a.item.sku.localeCompare(b.item.sku, undefined, { numeric: true }),
      );
  }, [items, ix, bom, exclude, nearKey, px, wantedKey]);

  const results = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    const hits = terms.length ? ranked.filter((r) => terms.every((t) => r.hay.includes(t))) : ranked;
    // An exact SKU goes first, whatever its group.
    const exact = hits.findIndex((r) => r.item.sku.toLowerCase() === query.trim().toLowerCase());
    return (exact > 0 ? [hits[exact], ...hits.slice(0, exact), ...hits.slice(exact + 1)] : hits).slice(0, LIMIT);
  }, [ranked, query]);

  const groupName = (rank: number, item: Item) =>
    rank === 0
      ? `Same group: ${partGroups.find((g) => g.id === item.part_group_id)?.name ?? ""}`
      : rank === 1
        ? `In ${wanted.map((id) => px.projectById.get(id)?.name ?? "").join(", ")}`
        : rank === 2
          ? `${codes.map((c) => `${c}-…`).join(", ")} not on a BOM yet`
          : rank === 3
            ? "Shares a tag"
            : "Everything else";

  const pick = (item: Item | undefined) => {
    if (!item) return;
    onPick(item);
    setQuery("");
    setCursor(0);
    input.current?.focus();
  };

  return (
    <div className={cx("relative", className)}>
      <div className="relative">
        <span className="pointer-events-none absolute top-2 left-2.5 text-faint">{icon ?? <Search className="size-4" />}</span>
        <Input
          ref={input}
          autoFocus={autoFocus}
          value={query}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onChange={(e) => {
            setQuery(e.target.value);
            setCursor(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") setCursor((c) => Math.min(results.length - 1, c + 1));
            else if (e.key === "ArrowUp") setCursor((c) => Math.max(0, c - 1));
            else if (e.key === "Enter") pick(results[cursor]?.item);
            else if (e.key === "Escape") {
              if (query) setQuery("");
              else {
                onCancel?.();
                e.currentTarget.blur();
              }
            } else return;
            e.preventDefault();
            e.stopPropagation();
          }}
          className="pl-8"
        />
      </div>
      {(open || inline) && (
        <div
          className={cx(
            "overflow-y-auto rounded-lg border border-line bg-surface py-1",
            inline ? "mt-1 max-h-64" : "absolute inset-x-0 top-9 z-30 max-h-80 shadow-pop",
          )}
        >
          {results.length === 0 && <div className="px-3 py-2 text-muted">No parts match</div>}
          {results.map((r, i) => (
            <div key={r.item.id}>
              {(i === 0 || results[i - 1].rank !== r.rank || (r.rank === 0 && results[i - 1].item.part_group_id !== r.item.part_group_id)) && (
                <div className="px-3 pt-1.5 pb-0.5 text-[11px] font-medium tracking-wide text-faint uppercase">{groupName(r.rank, r.item)}</div>
              )}
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setCursor(i)}
                onClick={() => pick(r.item)}
                className={cx("flex w-full items-center gap-2 px-3 py-1 text-left", i === cursor && "bg-hover")}
              >
                <Thumb src={r.item.photo_url} size={20} />
                <span className="w-36 shrink-0 truncate font-mono text-[12px]">{r.item.sku}</span>
                <span className="min-w-0 flex-1 truncate">{r.item.name}</span>
                {typeOf(r.item) !== "material" && <Badge>{typeLabel(r.item)}</Badge>}
                {r.item.lifecycle === "eol" || r.item.lifecycle === "obsolete" ? <Badge tone="warn">{r.item.lifecycle.toUpperCase()}</Badge> : null}
                <span className="w-16 shrink-0 text-right font-mono text-[12px] text-muted">{money(r.item.std_cost ?? ix.vendorPrice.get(r.item.id))}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
